import { ApprovalTaskStatues, Roles } from "common";
import { maxBy } from "lodash";
import { approvalTaskStatuesOption } from "pages/ApprovalDashboard/ApprovalDashboard.data";
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useGetApprovalTaskQuery } from "redux/services/approvalProcessor";
import { useGetProfileQuery } from "redux/services/membership";
import { getOrganisationMembership } from "utils";

interface ApprovalDetailsServiceResult {
  getApprovalStatus: () =>
    | {
        value: ApprovalTaskStatues;
        label: string;
        data: {
          colorScheme: string;
        };
      }
    | undefined;
}

export const useApprovalDetailsService = (): ApprovalDetailsServiceResult => {
  const { paymentId } = useParams() as any;

  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );

  const orgToken = membershipOrganisation?.token;

  const { data: approvalTasks } = useGetApprovalTaskQuery(
    {
      referenceNumber: paymentId,
      orgToken,
    },
    { refetchOnMountOrArgChange: true }
  );

  const getApprovalStatus = useCallback(() => {
    const approvalTask = approvalTasks?.data[0];

    const status = approvalTaskStatuesOption.find(
      (option) => option.value === approvalTask?.status
    );

    if (status?.value === ApprovalTaskStatues.PROCESSING) {
      let statusLabel = approvalTaskStatuesOption.find(
        (option) => option.value === ApprovalTaskStatues.CHECKER_REJECTED
      )?.label;
      let statusColorScheme = approvalTaskStatuesOption.find(
        (option) => option.value === ApprovalTaskStatues.CHECKER_REJECTED
      )?.data?.colorScheme;

      const listApprover = approvalTasks?.data[0].approvalTasks?.filter(
        (approver) => approver.status === ApprovalTaskStatues.APPROVED
      );
      const approver = maxBy(listApprover, (approver) => approver?.levelOrder);

      if (approver?.levelId === Roles.BusinessAdmin) {
        statusLabel = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.APPROVER_APPROVED
        )?.label;
        statusColorScheme = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.APPROVER_APPROVED
        )?.data?.colorScheme;
      }

      if (approver?.levelId === Roles.BusinessApprover) {
        statusLabel = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.CHECKER_APPROVED
        )?.label;
        statusColorScheme = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.CHECKER_APPROVED
        )?.data?.colorScheme;
      }

      if (approver?.levelId === Roles.BUApprover) {
        statusLabel = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.APPROVER_APPROVED
        )?.label;
        statusColorScheme = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.APPROVER_APPROVED
        )?.data?.colorScheme;
      }

      status.label = statusLabel ?? status.label;
      status.data.colorScheme = statusColorScheme ?? status.data.colorScheme;
    }

    if (status?.value === ApprovalTaskStatues.REJECTED) {
      let statusLabel = approvalTaskStatuesOption.find(
        (option) => option.value === ApprovalTaskStatues.CHECKER_REJECTED
      )?.label;
      let statusColorScheme = approvalTaskStatuesOption.find(
        (option) => option.value === ApprovalTaskStatues.CHECKER_REJECTED
      )?.data?.colorScheme;

      const listRejecter = approvalTasks?.data[0].approvalTasks.filter(
        (approver) => approver.status === ApprovalTaskStatues.REJECTED
      );
      const rejecter = maxBy(listRejecter, (approver) => approver?.levelOrder);

      if (rejecter?.levelId === Roles.BusinessAdmin) {
        statusLabel = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.APPROVER_DISAPPROVED
        )?.label;
        statusColorScheme = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.APPROVER_DISAPPROVED
        )?.data?.colorScheme;
      }

      if (rejecter?.levelId === Roles.PaymentOps) {
        statusLabel = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.PAYMENT_OPS_DECLINED
        )?.label;
        statusColorScheme = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.PAYMENT_OPS_DECLINED
        )?.data?.colorScheme;
      }

      if (rejecter?.levelId === Roles.BUApprover) {
        statusLabel = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.APPROVER_DISAPPROVED
        )?.label;
        statusColorScheme = approvalTaskStatuesOption.find(
          (option) => option.value === ApprovalTaskStatues.APPROVER_DISAPPROVED
        )?.data?.colorScheme;
      }

      status.label = statusLabel ?? status.label;
      status.data.colorScheme = statusColorScheme ?? status.data.colorScheme;
    }

    return status;
  }, [approvalTasks]);

  return {
    getApprovalStatus,
  };
};
